import CddPeriodService from "@/services/cdd/CddPeriodService";
import CddRunService from "@/services/cdd/CddRunService";
import CddEntityService from "@/services/cdd/CddEntityService";
import CddDecisionConfigService from "@/services/cdd/CddDecisionConfigService";

// Module-level timer references — not in Vuex state to avoid serialization issues
// and to prevent the dual-dispatch race condition.
let _pollingInterval = null;
let _pollingTimeout = null;

export default {
  // ─── Dashboard ────────────────────────────────────────────────────────────

  async fetchDashboardStats({ commit }) {
    try {
      const res = await CddRunService.getDashboardStats();
      commit("SET_DASHBOARD_STATS", res.data?.result ?? res.data);
    } catch (e) {
      commit("SET_DASHBOARD_STATS", null);
    }
  },

  async fetchStatusSummary({ commit }, params) {
    try {
      const res = await CddRunService.getStatusSummary(params);
      commit("SET_STATUS_SUMMARY", res.data?.result ?? res.data);
    } catch (e) {
      commit("SET_STATUS_SUMMARY", null);
    }
  },

  // Yanıt kodu tanımları (CddPill renkleri için global olarak yüklenir)
  async fetchResponseCodes({ commit }) {
    try {
      const res = await CddDecisionConfigService.getResponseCodes();
      commit("SET_RESPONSE_CODES", res.data?.result ?? res.data);
    } catch (e) {
      commit("SET_RESPONSE_CODES", []);
    }
  },

  // ─── Periods ──────────────────────────────────────────────────────────────

  async fetchPeriods({ commit }, params = {}) {
    commit("SET_PERIODS_LOADING", true);
    try {
      const res = await CddPeriodService.getAll(params);
      const result = res.data?.result ?? {};
      const list = result.items ?? result.data ?? [];
      commit("SET_PERIODS", {
        data: Array.isArray(list) ? list : [],
        pagination: { total: result.total ?? 0 },
      });
    } catch (e) {
      commit("SET_PERIODS", { data: [], pagination: { total: 0 } });
    } finally {
      commit("SET_PERIODS_LOADING", false);
    }
  },

  async fetchPeriodById({ commit }, id) {
    const res = await CddPeriodService.getById(id);
    commit("SET_CURRENT_PERIOD", res.data?.result ?? res.data);
  },

  async triggerPeriod({ commit }, id) {
    try {
      const res = await CddPeriodService.trigger(id);
      commit("SET_TOAST", { message: "Tarama başlatıldı.", type: "success" });
      return res.data?.result ?? null;
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.Message ||
        "Tarama başlatılamadı.";
      commit("SET_TOAST", { message: msg, type: "error" });
      return null;
    }
  },

  async previewQuery({ commit }, { queryId }) {
    try {
      const res = await CddPeriodService.previewQuery(queryId);
      return res.data?.result ?? res.data;
    } catch (e) {
      commit("SET_TOAST", {
        message: "Önizleme henüz desteklenmiyor.",
        type: "warning",
      });
      return null;
    }
  },

  async deletePeriod({ commit, dispatch }, id) {
    try {
      await CddPeriodService.delete(id);
      commit("SET_TOAST", { message: "Dönem silindi.", type: "success" });
      await dispatch("fetchPeriods");
    } catch (e) {
      commit("SET_TOAST", { message: "Dönem silinemedi.", type: "error" });
    }
  },

  async activatePeriod({ commit, dispatch }, id) {
    try {
      await CddPeriodService.activate(id);
      commit("SET_TOAST", { message: "Periyot aktifleştirildi.", type: "success" });
      await dispatch("fetchPeriods");
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.Message ||
        "Periyot aktifleştirilemedi.";
      commit("SET_TOAST", { message: msg, type: "error" });
    }
  },

  async pausePeriod({ commit, dispatch }, id) {
    try {
      await CddPeriodService.pause(id);
      commit("SET_TOAST", { message: "Periyot pasife alındı.", type: "success" });
      await dispatch("fetchPeriods");
    } catch (e) {
      commit("SET_TOAST", { message: "Periyot pasife alınamadı.", type: "error" });
    }
  },

  // ─── Runs ─────────────────────────────────────────────────────────────────

  async fetchRuns({ commit }, params = {}) {
    commit("SET_RUNS_LOADING", true);
    try {
      const res = await CddRunService.getAll(params);
      const result = res.data?.result ?? {};
      const list = result.items ?? result.data ?? [];
      commit("SET_RUNS", {
        data: Array.isArray(list) ? list : [],
        pagination: { total: result.total ?? 0 },
      });
    } catch (e) {
      commit("SET_RUNS", { data: [], pagination: { total: 0 } });
    } finally {
      commit("SET_RUNS_LOADING", false);
    }
  },

  async fetchRunById({ commit }, id) {
    const res = await CddRunService.getById(id);
    commit("SET_CURRENT_RUN", res.data?.result ?? res.data);
  },

  async fetchRunProgress({ commit }, id) {
    const res = await CddRunService.getProgress(id);
    commit("SET_RUN_PROGRESS", res.data?.result ?? res.data);
  },

  startPolling({ commit, state, dispatch }, id) {
    // Clear any existing interval first (module-level reference, no race)
    if (_pollingInterval) {
      clearInterval(_pollingInterval);
      _pollingInterval = null;
    }
    if (_pollingTimeout) {
      clearTimeout(_pollingTimeout);
      _pollingTimeout = null;
    }
    commit("SET_POLLING_TIMED_OUT", false);

    const MAX_POLL_MS = 30 * 60 * 1000; // 30 minutes
    _pollingTimeout = setTimeout(() => {
      if (_pollingInterval) {
        clearInterval(_pollingInterval);
        _pollingInterval = null;
      }
      commit("SET_POLLING_TIMED_OUT", true);
      commit("CLEAR_POLLING");
    }, MAX_POLL_MS);

    _pollingInterval = setInterval(() => {
      dispatch("fetchRunProgress", id);
    }, 5000);

    // Keep Vuex state in sync for components that watch it
    commit("SET_POLLING_INTERVAL", _pollingInterval);
  },

  stopPolling({ commit }) {
    if (_pollingInterval) {
      clearInterval(_pollingInterval);
      _pollingInterval = null;
    }
    if (_pollingTimeout) {
      clearTimeout(_pollingTimeout);
      _pollingTimeout = null;
    }
    commit("CLEAR_POLLING");
  },

  async fetchRunResults({ commit }, { id, params }) {
    try {
      const res = await CddRunService.getResults(id, params);
      const result = res.data?.result ?? {};
      const list = result.items ?? result.data ?? [];
      commit("SET_RUN_RESULTS", {
        data: Array.isArray(list) ? list : [],
        pagination: { total: result.total ?? 0 },
      });
    } catch (e) {
      commit("SET_RUN_RESULTS", { data: [], pagination: { total: 0 } });
    }
  },

  async cancelRun({ commit, dispatch }, id) {
    await CddRunService.cancel(id);
    await dispatch("fetchRunById", id);
  },

  async retryRun({ commit, dispatch }, id) {
    await CddRunService.retry(id);
    await dispatch("fetchRunById", id);
  },

  async fetchResultDetail({ commit }, { runId, resultId }) {
    try {
      commit("OPEN_RESULT_MODAL", resultId);
      const res = await CddRunService.getResultDetail(runId, resultId);
      commit("SET_RESULT_MODAL_DATA", res.data?.result ?? res.data);
    } catch (e) {
      commit("SET_TOAST", {
        message: "Sonuç detayı yüklenemedi.",
        type: "error",
      });
      commit("CLOSE_RESULT_MODAL");
    }
  },

  closeResultModal({ commit }) {
    commit("CLOSE_RESULT_MODAL");
  },

  // ─── Customers ────────────────────────────────────────────────────────────

  // payload: string (entityKey) veya { entityKey, entityId }. entityId, farklı entity
  // tiplerinde aynı key değerinin (customer 4 vs havayolu 4) karışmasını engeller.
  async fetchEntity({ commit }, payload) {
    const entityKey = typeof payload === "object" ? payload.entityKey : payload;
    const entityId = typeof payload === "object" ? payload.entityId : undefined;
    const res = await CddEntityService.getProfile(entityKey, entityId);
    commit("SET_CURRENT_ENTITY", res.data?.result ?? res.data);
  },

  async fetchEntityHistory({ commit }, payload) {
    const entityKey = typeof payload === "object" ? payload.entityKey : payload;
    const entityId = typeof payload === "object" ? payload.entityId : undefined;
    const page = (typeof payload === "object" && payload.page) ? payload.page : 1;
    const pageSize = (typeof payload === "object" && payload.pageSize) ? payload.pageSize : 20;
    commit("SET_ENTITY_HISTORY_LOADING", true);
    try {
      const res = await CddEntityService.getHistory(entityKey, { page, pageSize }, entityId);
      const result = res.data?.result ?? {};
      const list = Array.isArray(result) ? result : (result.items ?? []);
      const total = Array.isArray(result) ? list.length : (result.total ?? 0);
      commit("SET_ENTITY_HISTORY", { data: Array.isArray(list) ? list : [], total });
    } catch (e) {
      commit("SET_ENTITY_HISTORY", { data: [], total: 0 });
    } finally {
      commit("SET_ENTITY_HISTORY_LOADING", false);
    }
  },

  async fetchEntityLiveData({ commit }, payload) {
    const entityKey = typeof payload === "object" ? payload.entityKey : payload;
    const entityId = typeof payload === "object" ? payload.entityId : undefined;
    try {
      const res = await CddEntityService.getLiveData(entityKey, entityId);
      const data = res.data?.result ?? null;
      commit("SET_ENTITY_LIVE_DATA", Array.isArray(data) ? data : null);
    } catch (e) {
      commit("SET_ENTITY_LIVE_DATA", null);
    }
  },

  // ─── Score Change Requests ────────────────────────────────────────────────

  async fetchScoreChangeRequests({ commit }, { entityType, entityKey }) {
    try {
      const res = await CddEntityService.getScoreChangeRequests(
        entityType,
        entityKey,
      );
      const list = res.data?.result ?? [];
      commit("SET_SCORE_CHANGE_REQUESTS", Array.isArray(list) ? list : []);
    } catch (e) {
      commit("SET_SCORE_CHANGE_REQUESTS", []);
    }
  },

  async createScoreChangeRequest(
    { commit, dispatch },
    { dto, entityType, entityKey },
  ) {
    try {
      const res = await CddEntityService.createScoreChangeRequest(dto);
      if (res.data?.operationStatus) {
        commit("SET_TOAST", {
          message: "Skor artırım talebi oluşturuldu.",
          type: "success",
        });
        commit("SET_SCORE_CHANGE_DIALOG", false);
        await dispatch("fetchScoreChangeRequests", { entityType, entityKey });
        await dispatch("fetchEntity", entityKey);
        return true;
      } else {
        commit("SET_TOAST", {
          message: res.data?.message || "Talep oluşturulamadı.",
          type: "error",
        });
        return false;
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.Message ||
        "Talep oluşturulamadı.";
      commit("SET_TOAST", { message: msg, type: "error" });
      return false;
    }
  },

  async reviewScoreChangeRequest(
    { commit, dispatch },
    { requestId, action, reviewNote, entityType, entityKey },
  ) {
    try {
      const res = await CddEntityService.reviewScoreChangeRequest(requestId, {
        action,
        reviewNote,
      });
      if (res.data?.operationStatus) {
        const msg =
          action === "APPROVE"
            ? "Talep onaylandı, skor güncellendi."
            : "Talep reddedildi.";
        commit("SET_TOAST", { message: msg, type: "success" });
        await dispatch("fetchScoreChangeRequests", { entityType, entityKey });
        await dispatch("fetchEntity", entityKey);
        await dispatch("fetchEntityHistory", entityKey);
        return true;
      } else {
        commit("SET_TOAST", {
          message: res.data?.message || "İşlem başarısız.",
          type: "error",
        });
        return false;
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.Message ||
        "İşlem başarısız.";
      commit("SET_TOAST", { message: msg, type: "error" });
      return false;
    }
  },

  async cancelScoreChangeRequest(
    { commit, dispatch },
    { requestId, entityType, entityKey },
  ) {
    try {
      const res = await CddEntityService.cancelScoreChangeRequest(requestId);
      if (res.data?.operationStatus) {
        commit("SET_TOAST", {
          message: "Talep iptal edildi.",
          type: "success",
        });
        await dispatch("fetchScoreChangeRequests", { entityType, entityKey });
        await dispatch("fetchEntity", entityKey);
        return true;
      } else {
        commit("SET_TOAST", {
          message: res.data?.message || "İptal edilemedi.",
          type: "error",
        });
        return false;
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.response?.data?.Message ||
        "İptal edilemedi.";
      commit("SET_TOAST", { message: msg, type: "error" });
      return false;
    }
  },

  clearCurrentPeriod({ commit }) {
    commit("CLEAR_CURRENT_PERIOD");
  },
};
